import { ReactNode } from 'react';
import { Token } from 'prism-react-renderer';

import {
  CodeVariable,
  CodeVariableMap,
  PlainCodeVariable,
} from '@components/patterns/Code/types';

export interface ProcessLineParams {
  line: Token[];
  pattern: RegExp;
  variables?: CodeVariableMap;
}

/**
 * Processes a line of code to replace variables with their values.
 *
 * Since the line is already tokenized by Prism, we need to find the matches inside and across tokens,
 * then inject new tokens for the custom variables.
 */
export function processLine({
  line,
  pattern,
  variables,
}: ProcessLineParams): Token[] {
  // If no variables, return early
  if (!variables) {
    return line;
  }

  // Find all matches for custom variables in the raw (plain text) line
  const rawLine = line.map((t) => t.content).join('');
  const matches = Array.from(rawLine.matchAll(pattern) || []).map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
    content: match[0],
    injected: false,
  }));

  // If no matches, return early
  if (!matches.length) {
    return line;
  }

  const result: Token[] = [];

  // Index of the current token in line
  let index = 0;
  // Cursor position in the text (based on last processed token)
  let cursor = 0;
  for (const token of line) {
    // Get the text to process (offset from the current cursor position)
    const offset = cursor - index;
    const text = token.content.slice(offset);

    const end = text.length;
    let start = 0;

    // Search for matches in the token
    for (const match of matches) {
      if (match.injected) continue;
      if (match.start >= cursor + start && match.start <= cursor + end) {
        // If match is found, add the text before the match and the custom variable token
        const content = text.slice(start, match.start - cursor);
        result.push(
          { ...token, content },
          { types: [...token.types, 'custom-variable'], content: match.content }
        );
        start += content.length + match.content.length;
        // Mark the match as injected to avoid processing it again
        match.injected = true;
      }
    }

    // Add the remaining text to the result
    const remaining = text.slice(start);
    if (remaining.length > 0) {
      result.push({ ...token, content: remaining });
      start += remaining.length;
    }

    // Update the cursor based on the text processed
    cursor += start;
    // Update the index based on the token length
    index += token.content.length;
  }

  return result;
}

export function isCustomVariable(token: Token): boolean {
  return token.types.includes('custom-variable');
}

export function getRenderedVariable(variable: CodeVariable): ReactNode {
  if (typeof variable === 'object') {
    return variable.rendered;
  }
  return variable;
}

export function getPlainVariable(variable: CodeVariable): PlainCodeVariable {
  if (typeof variable === 'object') {
    return variable.plain;
  }
  return variable;
}

export function getPlainText(
  text: string,
  variables?: CodeVariableMap
): string {
  if (!variables) return text;

  let content = text;
  for (const [key, variable] of Object.entries(variables)) {
    // A function, so `$&` and similar in the value aren't read as patterns
    content = content.replaceAll(`$${key}`, () =>
      getPlainVariable(variable).toString()
    );
  }
  return content;
}
